import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle, Clock, XCircle, ChevronRight } from 'lucide-react';

const ProfileCompleteness = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/profile/completeness')
      .then(res => setData(res.data))
      .catch(() => {});
  }, []);

  if (!data) return null;

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'rejected') return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-700';
    if (status === 'rejected') return 'bg-red-100 text-red-700';
    if (status === 'optional') return 'bg-gray-100 text-gray-500';
    return 'bg-amber-100 text-amber-700';
  };

  if (data.percentage === 100) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6" data-testid="profile-completeness">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="font-medium text-green-800">Profile Complete</span>
          <Badge className="bg-green-100 text-green-700 ml-auto">100%</Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 shadow-sm" data-testid="profile-completeness">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Complete Your Profile</h3>
        <Badge className={data.percentage >= 75 ? 'bg-green-100 text-green-700' : data.percentage >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
          {data.percentage}%
        </Badge>
      </div>
      <Progress value={data.percentage} className="h-2 mb-4" />
      <div className="space-y-2">
        {data.items.filter(i => i.status !== 'optional' || i.status !== 'completed').map((item) => (
          <div
            key={item.id}
            className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
              item.status === 'completed' ? 'bg-green-50/50' : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
            }`}
            onClick={() => item.status !== 'completed' && navigate(item.action)}
            data-testid={`completeness-item-${item.id}`}
          >
            {getStatusIcon(item.status)}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${item.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                {item.label}
              </p>
              <p className="text-xs text-gray-400 truncate">{item.description}</p>
            </div>
            {item.status !== 'completed' && (
              <Button size="sm" variant="ghost" className="text-cyan-600 shrink-0 text-xs h-7">
                {item.action_label} <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileCompleteness;
